/**
 * kd-algolia-ranking-tabs: v2 TOP カテゴリ別ランキング（タブ切替）
 *
 * sections/v2-top-ranking-tabs.liquid から子要素として渡される
 * <script type="application/json" data-tabs-config> をパースし、
 * 全タブ分を Algolia multipleQueries で一括取得、アクティブタブの hits を
 * Liquid 側 [data-hits-container] に流し込む。
 *
 * Lit は振る舞いレイヤーに徹し HTML 構造は Liquid が担う。
 * - render() は nothing を返す
 * - タブ <button role="tab"> は Liquid 側に並んでいる前提
 * - hit 値は applyDisplayDataToFragment 経由で data-field セレクタに流す
 *   （流し込みロジックは kd-algolia-carousel と共通の carousel.logic.ts に集約）
 */
import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { AlgoliaHit } from './algolia-hit-template';
import {
  getAlgoliaConfig,
  createSearchClient,
  waitForAlgoliaLibs,
} from './utils/algolia-client';
import { applyDisplayDataToFragment } from './carousel.logic';
import {
  buildBatchQueries,
  parseTabsConfig,
  TAB_ACTIVE_CLASSES,
  TAB_INACTIVE_CLASSES,
  type TabConfig,
  type BatchQuery,
} from './ranking-tabs.logic';
import { SELECTORS } from '../../constants/selectors';

interface SearchResult {
  hits: AlgoliaHit[];
  nbHits: number;
}

@customElement('kd-algolia-ranking-tabs')
export class KdAlgoliaRankingTabs extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Number, attribute: 'display-num' })
  displayNum = 10;

  @property({ type: Boolean, attribute: 'hide-compare-price' })
  hideComparePrice = false;

  // true なら自前で Algolia へ search() せず、TOP orchestrator が
  // getDeferredQueries() / applyDeferredResults() 経由で 1 リクエストに
  // 束ねて駆動する（複数セクションの並列リクエストによる課金増を避ける）。
  // PDP 等 orchestrator が無いページでは false のまま自前 search する。
  @property({ type: Boolean, attribute: 'data-deferred', reflect: true })
  deferred = false;

  private _tabs: TabConfig[] = [];
  private _hitsByTab: AlgoliaHit[][] = [];
  // タブ別の総件数（nbHits）。見出し右「全N商品」+ すべて見るボタン「（N商品）」に反映。
  private _nbHitsByTab: number[] = [];
  private _activeIndex = 0;
  private _loaded = false;

  // タブごとに addEventListener したハンドラを保持し disconnectedCallback で
  // removeEventListener する。インライン関数だと remove できないため
  // (button, click ハンドラ, keydown ハンドラ) を組にして覚えておく。
  private _tabHandlers: Array<{
    tab: HTMLButtonElement;
    click: () => void;
    keydown: (e: KeyboardEvent) => void;
  }> = [];

  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    // Shopify テーマエディタで section をドラッグ移動すると DOM が
    // remove → re-insert される。Algolia への再リクエストは無駄なので
    // _loaded フラグで _load() の再実行は防ぐが、disconnect 時に外した
    // タブハンドラだけは再 bind する必要がある。
    if (this._loaded) {
      this._bindTabHandlers();
      return;
    }
    this._loaded = true;
    // deferred モードなら自前 search せず TOP orchestrator からの駆動を待つ
    if (this.deferred) return;
    await this._load();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._unbindTabHandlers();
  }

  /**
   * tabs 設定と config から multipleQueries 配列を組み立てる（同期・副作用は _tabs 設定のみ）。
   * 設定不正 / 未接続時は section を隠して [] を返す。
   * self-search パスと orchestrator 集約パスで共通利用する。
   */
  private _buildQueries(): BatchQuery[] {
    this._tabs = this._readTabsConfig();
    if (this._tabs.length === 0) {
      this._hideSection();
      return [];
    }
    const config = getAlgoliaConfig();
    if (!config?.connected) {
      this._hideSection();
      return [];
    }
    return buildBatchQueries(this._tabs, config.indexName, this.displayNum, config.customer);
  }

  /**
   * 自身分の multipleQueries results をタブ別 hits に変換して描画する。
   * self / 集約 共通。全タブ 0 件なら section ごと非表示。
   */
  private _applyResults(results: Array<SearchResult | undefined>): void {
    this._hitsByTab = results.map((r) => r?.hits ?? []);
    this._nbHitsByTab = results.map((r) => r?.nbHits ?? 0);
    if (this._hitsByTab.every((h) => h.length === 0)) {
      this._hideSection();
      return;
    }
    this._bindTabHandlers();
    this._renderActiveTab();
    this._updateResultCount();
    // 初期表示時点で list_url が Liquid 側 fallback ('/collections/all') と
    // _tabs[0].listUrl が乖離している可能性があるため明示的に同期
    this._updateMoreLink();
  }

  /** orchestrator 集約用 public API: 自身のクエリ配列を返す（無効時は [] + 非表示）。 */
  getDeferredQueries(): BatchQuery[] {
    return this._buildQueries();
  }

  /** orchestrator 集約用 public API: 自身分の results スライスを受け取り描画する。 */
  applyDeferredResults(results: Array<SearchResult | undefined>): void {
    this._applyResults(results);
  }

  /** orchestrator 集約用 public API: バッチ失敗時に section を隠す。 */
  handleDeferredFailure(): void {
    this._hideSection();
  }

  private async _load(): Promise<void> {
    const queries = this._buildQueries();
    if (queries.length === 0) return;
    try {
      await waitForAlgoliaLibs();
      const searchClient = createSearchClient({
        cacheKey: 'algolia-responses-ranking-tabs',
        maxEntries: 50,
      });
      if (!searchClient) {
        this._hideSection();
        return;
      }
      const response = (await searchClient.search(queries)) as {
        results: SearchResult[];
      };
      this._applyResults(response.results);
    } catch {
      this._hideSection();
    }
  }

  private _readTabsConfig(): TabConfig[] {
    const el = this.querySelector<HTMLScriptElement>(SELECTORS.RANKING_TABS.CONFIG);
    return parseTabsConfig(el?.textContent ?? '');
  }

  private _bindTabHandlers(): void {
    // 既に bind 済みなら何もしない（reconnect 時に _bindTabHandlers が
    // 二重に呼ばれた場合のガード）。
    if (this._tabHandlers.length > 0) return;
    const tabs = this._getTabButtons();
    tabs.forEach((tab, i) => {
      const click = (): void => this._selectTab(i);
      const keydown = (e: KeyboardEvent): void => this._onTabKeyDown(e, i, tabs.length);
      tab.addEventListener('click', click);
      tab.addEventListener('keydown', keydown);
      this._tabHandlers.push({ tab, click, keydown });
    });
  }

  private _unbindTabHandlers(): void {
    this._tabHandlers.forEach(({ tab, click, keydown }) => {
      tab.removeEventListener('click', click);
      tab.removeEventListener('keydown', keydown);
    });
    this._tabHandlers = [];
  }

  private _onTabKeyDown(e: KeyboardEvent, i: number, total: number): void {
    // WAI-ARIA tablist パターンのキーボード操作
    // ←/→ で循環、Home/End で先頭/末尾へ
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % total;
    else if (e.key === 'ArrowLeft') next = (i - 1 + total) % total;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = total - 1;
    if (next < 0) return;
    e.preventDefault();
    this._selectTab(next);
    this._getTabButtons()[next]?.focus();
  }

  private _selectTab(i: number): void {
    if (i === this._activeIndex) return;
    if (i < 0 || i >= this._tabs.length) return;
    this._activeIndex = i;
    this._renderActiveTab();
    this._updateResultCount();
    this._updateTabStates();
    this._updateMoreLink();
  }

  /**
   * 見出し右「全N商品」+ すべて見るボタン「（N商品）」を、アクティブタブの
   * 総件数（nbHits）に同期する。件数 0 のタブでは要素を隠す。
   * 「全N商品」span は section root 配下（custom element 外）にあるため closest(ROOT) 経由で参照。
   */
  private _updateResultCount(): void {
    const nbHits = this._nbHitsByTab[this._activeIndex] ?? 0;
    const root = this.closest<HTMLElement>(SELECTORS.RANKING_TABS.ROOT);
    const applyCount = (
      wrapper: HTMLElement | null | undefined,
      num: HTMLElement | null | undefined,
    ): void => {
      if (!wrapper) return;
      if (num && nbHits > 0) {
        num.textContent = String(nbHits);
        wrapper.hidden = false;
      } else {
        wrapper.hidden = true;
      }
    };
    // 見出し右「全N商品」（section root 配下）
    applyCount(
      root?.querySelector<HTMLElement>('[data-field="result-count"]'),
      root?.querySelector<HTMLElement>('[data-field="result-count-num"]'),
    );
    // すべて見るボタン内「（N商品）」（custom element 配下）
    applyCount(
      this.querySelector<HTMLElement>('[data-field="more-count"]'),
      this.querySelector<HTMLElement>('[data-field="more-count-num"]'),
    );
  }

  private _renderActiveTab(): void {
    const tpl = this.querySelector<HTMLTemplateElement>(SELECTORS.RANKING_TABS.HIT_TEMPLATE);
    const container = this.querySelector<HTMLElement>(SELECTORS.RANKING_TABS.HITS_CONTAINER);
    if (!tpl || !container) return;

    const hits = this._hitsByTab[this._activeIndex] ?? [];
    container.replaceChildren();
    hits.forEach((hit, i) => {
      const fragment = tpl.content.cloneNode(true) as DocumentFragment;
      applyDisplayDataToFragment(fragment, hit, i, {
        showRank: true,
        hideComparePrice: this.hideComparePrice,
      });
      container.appendChild(fragment);
    });
  }

  private _updateTabStates(): void {
    // aria-selected / tabindex / 見た目クラスをアクティブタブに同期
    // 見た目は「黒背景+白文字+border-0」⇄「白背景+ボーダー+オレンジ文字」を入れ替え。
    // クラス列は ranking-tabs.logic.ts の TAB_ACTIVE_CLASSES / TAB_INACTIVE_CLASSES
    // を真とする（Liquid 側初期 class とも一致させること）。
    // border-0 と border を「同時に存在させない」よう排他的に add/remove する
    // （Tailwind の CSS 出力順序に依存させないため）。
    const tabs = this._getTabButtons();
    tabs.forEach((tab, i) => {
      const active = i === this._activeIndex;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      if (active) {
        tab.classList.remove(...TAB_INACTIVE_CLASSES);
        tab.classList.add(...TAB_ACTIVE_CLASSES);
      } else {
        tab.classList.remove(...TAB_ACTIVE_CLASSES);
        tab.classList.add(...TAB_INACTIVE_CLASSES);
      }
    });
    // tabpanel が「いまどのタブのコンテンツか」をスクリーンリーダーに伝えるため
    // aria-labelledby をアクティブタブの id に同期する（WAI-ARIA APG tablist）
    const tabpanel = this.querySelector<HTMLElement>('[role="tabpanel"]');
    const activeTabId = tabs[this._activeIndex]?.id;
    if (tabpanel && activeTabId) {
      tabpanel.setAttribute('aria-labelledby', activeTabId);
    }
  }

  private _updateMoreLink(): void {
    // 「すべて見る」ボタンは SP/PC 共通でカード列直下（[data-more-link-button-wrapper]）に置く。
    // list_url 未設定タブに切り替わったときは wrapper ごと隠して余白が空のまま残るのを防ぐ。
    // 内側 <a> の href もアクティブタブの listUrl に同期する。
    const root = this.closest<HTMLElement>(SELECTORS.RANKING_TABS.ROOT);
    const moreLinkWrapper = root?.querySelector<HTMLElement>(SELECTORS.RANKING_TABS.MORE_LINK_BUTTON_WRAPPER);
    const moreLinkButton = moreLinkWrapper?.querySelector<HTMLAnchorElement>('a');
    const listUrl = this._tabs[this._activeIndex]?.listUrl;
    if (listUrl) {
      if (moreLinkButton) moreLinkButton.href = listUrl;
      if (moreLinkWrapper) moreLinkWrapper.hidden = false;
    } else {
      if (moreLinkWrapper) moreLinkWrapper.hidden = true;
    }
  }

  private _getTabButtons(): HTMLButtonElement[] {
    return Array.from(this.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  }

  private _hideSection(): void {
    // hit 0 件 / 接続失敗時はセクション全体を非表示にする（見出しだけ残るのを防ぐ）
    // Liquid 側 <section data-ranking-tabs-root> を data-* 属性で参照（class 名変更に強い疎結合）
    const section = this.closest<HTMLElement>(SELECTORS.RANKING_TABS.ROOT);
    if (section) section.hidden = true;
  }

  render() {
    return nothing;
  }
}
